import { useRef, useState, useCallback, useEffect } from 'react';

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;

export interface AudioError {
  message: string;
  code: 'network' | 'decode' | 'aborted' | 'unsupported' | 'unknown';
  canRetry: boolean;
}

export interface UseAudioPlayerReturn {
  // State
  isPlaying: boolean;
  isLoading: boolean;
  isBuffering: boolean;
  isStalled: boolean;
  currentTime: number;
  duration: number;
  progress: number; // 0-100
  playbackRate: number;
  volume: number;
  muted: boolean;
  error: AudioError | null;

  // Controls
  play: () => Promise<void>;
  pause: () => void;
  toggle: () => Promise<void>;
  seek: (time: number) => void;
  seekRelative: (delta: number) => void;
  seekToPercent: (percent: number) => void;
  setPlaybackRate: (rate: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  retry: () => void;

  // Ref
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

export function useAudioPlayer(
  src: string,
  initialTime?: number,
  onEnded?: () => void
): UseAudioPlayerReturn {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const onEndedRef = useRef(onEnded);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the ref updated with latest callback
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isStalled, setIsStalled] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [volume, setVolumeState] = useState(() => {
    try {
      const saved = localStorage.getItem('autopod-volume');
      return saved ? parseFloat(saved) : 1;
    } catch {
      return 1;
    }
  });
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem('autopod-muted') === 'true';
    } catch {
      return false;
    }
  });
  const [error, setError] = useState<AudioError | null>(null);

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Retry function
  const retry = useCallback(() => {
    const audio = audioRef.current;
    if (audio && src) {
      setError(null);
      setIsLoading(true);
      retryCountRef.current = 0;
      audio.load();
    }
  }, [src]);

  // Auto-retry for network errors
  const attemptRetry = useCallback(() => {
    const audio = audioRef.current;
    if (audio && retryCountRef.current < MAX_RETRY_ATTEMPTS) {
      retryCountRef.current += 1;
      console.log(`Retry attempt ${retryCountRef.current}/${MAX_RETRY_ATTEMPTS}`);

      retryTimeoutRef.current = setTimeout(() => {
        setError(null);
        setIsLoading(true);
        audio.load();
      }, RETRY_DELAY_MS);
    }
  }, []);

  // Clean up retry timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Set up audio element and event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Set initial volume and muted state
    audio.volume = volume;
    audio.muted = muted;

    const handleLoadStart = () => {
      setIsLoading(true);
      setIsBuffering(false);
      setIsStalled(false);
      setError(null);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      // Seek to initial time if provided
      if (initialTime && initialTime > 0 && initialTime < audio.duration) {
        audio.currentTime = initialTime;
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setIsBuffering(false);
      setIsStalled(false);
      // Reset retry count on successful load
      retryCountRef.current = 0;
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onEndedRef.current?.();
    };

    // Buffering state detection
    const handleWaiting = () => {
      setIsBuffering(true);
    };

    const handlePlaying = () => {
      setIsBuffering(false);
      setIsStalled(false);
    };

    const handleStalled = () => {
      setIsStalled(true);
    };

    const handleError = (e: Event) => {
      setIsLoading(false);
      setIsBuffering(false);
      const audioEl = e.target as HTMLAudioElement;
      const mediaError = audioEl?.error;

      let errorObj: AudioError = {
        message: 'Failed to load audio',
        code: 'unknown',
        canRetry: true,
      };

      if (mediaError) {
        switch (mediaError.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorObj = {
              message: 'Playback was stopped',
              code: 'aborted',
              canRetry: false,
            };
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorObj = {
              message: 'Network error - check your connection',
              code: 'network',
              canRetry: true,
            };
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorObj = {
              message: 'Audio decoding error',
              code: 'decode',
              canRetry: false,
            };
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorObj = {
              message: 'Audio format not supported',
              code: 'unsupported',
              canRetry: false,
            };
            break;
        }
      }

      // Auto-retry for network errors
      if (errorObj.canRetry && retryCountRef.current < MAX_RETRY_ATTEMPTS) {
        attemptRetry();
        return;
      }

      setError(errorObj);
    };

    const handleRateChange = () => {
      setPlaybackRateState(audio.playbackRate);
    };

    const handleVolumeChange = () => {
      setVolumeState(audio.volume);
      setMuted(audio.muted);
    };

    // Attach listeners
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('stalled', handleStalled);
    audio.addEventListener('error', handleError);
    audio.addEventListener('ratechange', handleRateChange);
    audio.addEventListener('volumechange', handleVolumeChange);

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('stalled', handleStalled);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('ratechange', handleRateChange);
      audio.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [initialTime, volume, muted, attemptRetry]);

  // Update src when it changes - wait a tick to ensure listeners are attached
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && src) {
      // Reset retry count when src changes
      retryCountRef.current = 0;
      // Use setTimeout to ensure the listener effect has completed
      const timer = setTimeout(() => {
        audio.src = src;
        audio.load();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [src]);

  // Control functions
  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (audio) {
      try {
        await audio.play();
        setError(null); // Clear any previous error on successful play
      } catch (err) {
        // Don't show error for autoplay policy - user just needs to click play
        if (err instanceof Error && err.name === 'NotAllowedError') {
          console.log('Autoplay blocked by browser - waiting for user interaction');
          return;
        }
        setError({
          message: 'Playback failed - check your connection',
          code: 'network',
          canRetry: true,
        });
      }
    }
  }, []);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
  }, []);

  const toggle = useCallback(async () => {
    if (isPlaying) {
      pause();
    } else {
      await play();
    }
  }, [isPlaying, play, pause]);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (audio && isFinite(time)) {
      audio.currentTime = Math.max(0, Math.min(time, audio.duration || 0));
    }
  }, []);

  const seekRelative = useCallback((delta: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, Math.min(audio.currentTime + delta, audio.duration || 0));
    }
  }, []);

  const seekToPercent = useCallback((percent: number) => {
    const audio = audioRef.current;
    if (audio && audio.duration) {
      audio.currentTime = (audio.duration * percent) / 100;
    }
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = rate;
    }
  }, []);

  const setVolume = useCallback((vol: number) => {
    const audio = audioRef.current;
    const clampedVol = Math.max(0, Math.min(1, vol));
    if (audio) {
      audio.volume = clampedVol;
    }
    try { localStorage.setItem('autopod-volume', clampedVol.toString()); } catch { /* sandboxed iframe */ }
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !audio.muted;
      try { localStorage.setItem('autopod-muted', audio.muted.toString()); } catch { /* sandboxed iframe */ }
    }
  }, []);

  return {
    isPlaying,
    isLoading,
    isBuffering,
    isStalled,
    currentTime,
    duration,
    progress,
    playbackRate,
    volume,
    muted,
    error,
    play,
    pause,
    toggle,
    seek,
    seekRelative,
    seekToPercent,
    setPlaybackRate,
    setVolume,
    toggleMute,
    retry,
    audioRef,
  };
}
